// frontend/src/pages/About.jsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Card from '../components/common/Card';
import Icon from '../components/common/Icon';
import AnimatedCounter from '../components/common/AnimatedCounter';

import { getAboutApi } from '../api/about.api';

// ============================================================
// داده‌های استاتیک
// ============================================================
const TEAM_VALUES = [
  {
    icon: 'eye',
    title: 'چشم‌انداز',
    description:
      'تبدیل شدن به مرجع اصلی توسعه فوتبال در استان با ایجاد بستری حرفه‌ای برای شناسایی و پرورش استعدادها.',
    color: 'emerald',
  },
  {
    icon: 'flag',
    title: 'رسالت',
    description:
      'ساماندهی، توسعه و ارتقای سطح فوتبال شهر از طریق مدیریت یکپارچه، شفافیت و حمایت از باشگاه‌ها و بازیکنان.',
    color: 'blue',
  },
  {
    icon: 'heart',
    title: 'ارزش‌ ها',
    description:
      'شفافیت، عدالت، حرفه‌ گرایی، توسعه پایدار، تعامل سازنده با جامعه فوتبال و ارتقای اخلاق ورزشی.',
    color: 'rose',
  },
];

const DEFAULT_STATS = [
  { key: 'activeClubs', label: 'باشگاه فعال', value: 0, icon: 'club' },
  { key: 'players', label: 'بازیکن ثبت‌ شده', value: 0, icon: 'users' },
  { key: 'matches', label: 'مسابقه برگزارشده', value: 0, icon: 'trophy' },
  { key: 'committees', label: 'کمیته تخصصی', value: 0, icon: 'award' },
];

const COLOR_VARIANTS = {
  emerald: 'from-emerald-400 to-emerald-600 bg-emerald-50 text-emerald-600',
  blue: 'from-blue-400 to-blue-600 bg-blue-50 text-blue-600',
  rose: 'from-rose-400 to-rose-600 bg-rose-50 text-rose-600',
  amber: 'from-amber-400 to-amber-600 bg-amber-50 text-amber-600',
  violet: 'from-violet-400 to-violet-600 bg-violet-50 text-violet-600',
};

const toPersianNumber = (value) => Number(value || 0).toLocaleString('fa-IR');

// ============================================================
// کامپوننت اصلی
// ============================================================
export default function About() {
  const [boardMembers, setBoardMembers] = useState([]);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [aboutData, setAboutData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const res = await getAboutApi();
        if (!mounted) return;

        const data = res.data.data;
        const apiStats = data.stats || {};

        setStats([
          { key: 'activeClubs', label: 'باشگاه فعال', value: apiStats.activeClubs || 0, icon: 'club' },
          { key: 'players', label: 'بازیکن ثبت‌ شده', value: apiStats.players || 0, icon: 'users' },
          { key: 'matches', label: 'مسابقه برگزارشده', value: apiStats.matches || 0, icon: 'trophy' },
          { key: 'committees', label: 'کمیته تخصصی', value: apiStats.committees || 0, icon: 'award' },
        ]);

        setBoardMembers(data.boardMembers || []);
        setAboutData(data);
      } catch (error) {
        console.error(error);
        setError(error.response?.data?.message || 'خطا در دریافت اطلاعات');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const base = process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || '';
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  return (
    <section className="min-h-screen py-16 animate-fade-in">
      <div className="container-page">

        {/* =========================================================
            HERO با افکت شیشه‌ای
        ========================================================= */}
        <div className="relative mb-12 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-8 text-white shadow-2xl md:p-12">
          {/* دکوراسیون پس‌زمینه */}
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-lime-500/10 blur-3xl" />
          {/* حذف pattern-grid.svg */}

          <div className="relative flex flex-col items-center gap-6 md:flex-row md:justify-between">
            <div className="animate-slide-up">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm font-bold text-emerald-300 backdrop-blur-sm">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                درباره هیئت فوتبال شهرستان پاوه
              </div>
              <h1 className="text-4xl font-black tracking-tight md:text-5xl lg:text-6xl">
                {aboutData.title || 'مرجع رسمی فوتبال شهر'}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-8 text-white/70 md:text-base">
                {aboutData.shortDescription ||
                  'هیئت فوتبال شهرستان پاوه با هدف توسعه و سازماندهی فوتبال شهر فعالیت می‌ کند.'}
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 px-6 py-4 backdrop-blur-sm animate-slide-down">
              <span className="text-xs font-bold text-white/50">تأسیس</span>
              <div className="text-2xl font-black">{aboutData.establishedYear || '۱۳۶۵'}</div>
            </div>
          </div>
        </div>

        {/* =========================================================
            ERROR
        ========================================================= */}
        {error && (
          <div className="mb-8 flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-600 animate-fade-in">
            <Icon name="alert-circle" className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* =========================================================
            چشم‌انداز، رسالت، ارزش‌ ها
        ========================================================= */}
        <div className="mb-12 grid gap-6 md:grid-cols-3">
          {TEAM_VALUES.map((item, index) => (
            <Card
              key={item.title}
              hover
              className="group relative overflow-hidden p-6 transition-all duration-500 hover:-translate-y-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-emerald-500/5 transition-transform duration-500 group-hover:scale-[3]" />
              <div className="relative">
                <div
                  className={`mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br ${COLOR_VARIANTS[item.color]} shadow-lg`}
                >
                  <Icon name={item.icon} className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-black text-text-dark">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-500">{item.description}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* =========================================================
            آمار کلی با شمارنده متحرک
        ========================================================= */}
        <div className="mb-12">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-black text-text-dark">آمار هیئت فوتبال</h2>
            <span className="rounded-full bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-600">
              اطلاعات به‌روز
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat, index) => (
              <Card
                key={stat.key}
                hover
                className="p-5 text-center transition-all duration-300 hover:shadow-lg"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-500 group-hover:text-white">
                  <Icon name={stat.icon} className="h-7 w-7" />
                </div>
                <div className="mt-3 text-3xl font-black text-emerald-600">
                  {loading ? (
                    <span className="inline-block h-8 w-12 animate-pulse rounded bg-slate-200" />
                  ) : (
                    <AnimatedCounter value={stat.value} duration={1500} />
                  )}
                </div>
                <div className="text-sm font-medium text-slate-500">{stat.label}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* =========================================================
            اعضای هیئت رئیسه (کارت‌های حرفه‌ای)
        ========================================================= */}
        {!loading && boardMembers.length > 0 && (
          <div className="mb-12">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-black text-text-dark">اعضای هیئت رئیسه</h2>
              <span className="rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-500">
                {boardMembers.length} عضو
              </span>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {boardMembers.map((member, index) => {
                const name = member.user?.fullName || member.fullName || 'بدون نام';
                const avatar = member.user?.avatar || member.avatar || null;
                const avatarUrl = getImageUrl(avatar);

                return (
                  <Card
                    key={member.id}
                    hover
                    className="group overflow-hidden p-0 transition-all duration-500 hover:-translate-y-2"
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <div className="relative h-28 bg-gradient-to-br from-emerald-500 to-emerald-700">
                      {/* دکوراسیون */}
                      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                      {/* حذف pattern-grid.svg */}

                      {/* آواتار */}
                      <div className="absolute -bottom-8 right-6">
                        <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-white shadow-xl transition-transform duration-300 group-hover:scale-105">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-200 text-2xl font-black text-emerald-700">
                              {name.charAt(0)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* برچسب سمت */}
                      <div className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/20 px-3 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
                        {member.type === 'BOARD' ? 'هیئت رئیسه' : 'کمیته'}
                      </div>
                    </div>

                    <div className="p-6 pt-12">
                      <h3 className="text-lg font-black text-text-dark">
                        {member.title || 'سمت'}
                      </h3>
                      <p className="text-sm text-slate-500">{name}</p>

                      {member.description && (
                        <p className="mt-3 text-sm leading-7 text-slate-400">
                          {member.description}
                        </p>
                      )}

                      {member.startDate && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                          <Icon name="calendar" className="h-3.5 w-3.5" />
                          <span>از {new Date(member.startDate).toLocaleDateString('fa-IR')}</span>
                          {member.endDate && (
                            <> تا {new Date(member.endDate).toLocaleDateString('fa-IR')}</>
                          )}
                        </div>
                      )}
                    </div>

                    {/* خط پایین */}
                    <div className="absolute inset-x-0 bottom-0 h-0.5 origin-right scale-x-0 bg-gradient-to-l from-emerald-500 to-lime-400 transition-transform duration-500 group-hover:scale-x-100" />
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* =========================================================
            توضیحات تکمیلی
        ========================================================= */}
        <div className="mb-12 rounded-3xl bg-white p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
              <Icon name="info" className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-black text-text-dark">درباره هیئت فوتبال شهرستان پاوه</h2>
          </div>
          <p className="mt-5 leading-9 text-slate-600">
            {aboutData.description ||
              'هیئت فوتبال شهرستان پاوه با هدف مدیریت، توسعه و ارتقای فوتبال فعالیت می‌ کند. این مجموعه با همکاری باشگاه‌ها، مربیان، بازیکنان و داوران، بستری حرفه‌ای برای رشد فوتبال در شهر فراهم کرده است. سامانه مدیریت هیئت فوتبال ستان پاوه به‌ عنوان ابزار اصلی این مجموعه، اطلاعات باشگاه‌ها، بازیکنان، مسابقات و رویدادها را به‌ صورت یکپارچه مدیریت می‌ کند.'}
          </p>
        </div>

        {/* =========================================================
            CTA - همکاری با هیئت فوتبال
        ========================================================= */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 to-lime-50 p-10 text-center shadow-sm">
          {/* دکوراسیون */}
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-200/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-lime-200/30 blur-3xl" />

          <div className="relative">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/20">
              <Icon name="handshake" className="h-8 w-8" />
            </div>
            <h2 className="mt-5 text-2xl font-black text-text-dark">همکاری با هیئت فوتبال</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-500">
              اگر باشگاه، مربی، بازیکن یا سازمان ورزشی هستید، خوشحال می‌ شویم با شما همکاری کنیم.
              برای اطلاعات بیشتر با ما تماس بگیرید.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-emerald-500/20 transition-all hover:-translate-y-1 hover:bg-emerald-700 hover:shadow-emerald-500/30"
              >
                ارتباط با ما
                <Icon name="arrow-left" className="h-4 w-4" />
              </Link>
              <Link
                to="/clubs"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-600 transition-all hover:-translate-y-1 hover:border-emerald-200 hover:bg-emerald-50"
              >
                مشاهده باشگاه‌ها
                <Icon name="shield" className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
